import {useEffect, useState} from 'react';
import close from './icons/close.svg';
import {isGoogleDriveVideoExtension, isVideoFile} from 'helpers/media';

import styles from './MediaView.module.scss';

interface MediaItem {
  id: number;
  file: string;
  thumbnail: string;
  medium: string;
  large: string;
  created_at: string;
  [key: string]: any; // Allow additional properties
}

interface Props {
  show?: boolean;
  onClose: () => any;
  mediaItems: MediaItem[];
}

function MediaView({show, onClose, mediaItems = []}: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset to first item when modal opens
  useEffect(() => {
    if (show) {
      setCurrentIndex(0);
    }
  }, [show]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentIndex, mediaItems.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
  };

  if (!show || mediaItems.length === 0) return null;

  const currentItem = mediaItems[currentIndex];
  const videoSource =
    currentItem.large || currentItem.file || currentItem.medium;

  const isVideo =
    isVideoFile(videoSource) || isGoogleDriveVideoExtension(videoSource);

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        {/* Close button */}
        <img
          className={styles.close}
          alt="close"
          src={close}
          onClick={onClose}
        />

        {/* Navigation buttons */}
        {mediaItems.length > 1 && (
          <>
            <button
              className={styles.navButton}
              onClick={goToPrevious}
              aria-label="Previous media"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              className={styles.navButton}
              onClick={goToNext}
              aria-label="Next media"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}

        {/* Media container */}
        <div className={styles.mediaContainer}>
          {!isVideo ? (
            <video
              className={styles.video}
              controls
              autoPlay
              muted
              src={videoSource}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              className={styles.image}
              alt={`${currentIndex + 1} of ${mediaItems.length}`}
              src={currentItem.large}
            />
          )}
        </div>

        {/* Media counter */}
        {mediaItems.length > 1 && (
          <div className={styles.counter}>
            {currentIndex + 1} / {mediaItems.length}
          </div>
        )}
      </div>
    </div>
  );
}

export default MediaView;
